/**
 * Пользователь из initData Telegram: имя, username и фото для профиля.
 * Разбор вынесен в чистую функцию без зависимостей, чтобы его можно было
 * проверить тестом в Node, где нет ни Telegram, ни window.
 */

import { t } from '../i18n';
export interface TelegramUser {
  id: number;
  firstName: string;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
  languageCode: string | null;
}

/** Поле `user` из строки initData. Любая неожиданность даёт null, а не исключение. */
export function parseInitDataUser(raw: string): TelegramUser | null {
  let json: string | null;
  try {
    json = new URLSearchParams(raw).get('user');
  } catch {
    return null;
  }
  if (!json) return null;

  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  const id = record['id'];
  if (typeof id !== 'number' || !Number.isFinite(id)) return null;

  const text = (key: string): string | null => {
    const field = record[key];
    return typeof field === 'string' && field.trim() !== '' ? field.trim() : null;
  };

  return {
    id,
    firstName: text('first_name') ?? '',
    lastName: text('last_name'),
    username: text('username'),
    photoUrl: text('photo_url'),
    languageCode: text('language_code'),
  };
}

/** Имя для карточки профиля: «Имя Фамилия», иначе @username, иначе заглушка. */
export function displayName(user: TelegramUser): string {
  const full = [user.firstName, user.lastName].filter((part) => part && part.length > 0).join(' ').trim();
  if (full) return full;
  if (user.username) return `@${user.username}`;
  return t('lib.user.fallback');
}

/** Инициалы для аватара без фото: первые буквы двух первых слов. */
export function initials(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter((word) => word.length > 0 && word !== '@')
    .slice(0, 2)
    .map((word) => [...word.replace(/^@/, '')][0]?.toUpperCase() ?? '');
  return letters.join('') || '?';
}
